
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import Image from 'next/image'
import { Icon } from '@/components/ui/Icon'
import { LOGO_ACCEPT, LOGO_MAX_BYTES, brandInitials, brandTint } from '@/lib/brands'
import { btn, cx, inputBase } from '@/lib/ui'
import type { BrandDTO } from '@/lib/types'
import { errorMessage } from '@/lib/client-api'

/**
 * The brand filter: one control that reads like the category select beside
 * it, opening into a searchable list.
 *
 * It used to be a strip of every manufacturer scrolled sideways. That works
 * for six brands and fails at sixty: the one you want is off-screen, the strip
 * gives no sense of how many there are, and a trackpad swipe is the only way
 * through. A closed control that names the current choice, and a list you can
 * type into, is the standard answer for a long single-choice filter — and it
 * keeps the top of the panel the same height however many brands arrive.
 *
 * Logos are shown where the platform has them, lettermarks elsewhere, and a
 * platform admin can still upload or replace a logo for the selected brand.
 */
export function BrandPicker({
  brands,
  names,
  counts,
  total,
  selected,
  onSelect,
  canManage,
  onBrandChange,
  onError,
}: {
  /** Brand rows the page knows about (logo URLs, websites). */
  brands: BrandDTO[]
  /** Every manufacturer in the list, catalogue and private alike. */
  names: string[]
  counts: ReadonlyMap<string, number>
  /** Devices across all brands, shown against "All brands". */
  total: number
  selected: 'all' | string
  onSelect: (brand: 'all' | string) => void
  /** Whether the current user may upload a logo for this manufacturer. */
  canManage: (name: string) => boolean
  onBrandChange: (brand: BrandDTO) => void
  onError: (message: string) => void
}) {
  const byName = useMemo(() => new Map(brands.map((brand) => [brand.name, brand])), [brands])
  const current = selected === 'all' ? null : (byName.get(selected) ?? null)
  const manageable = selected !== 'all' && canManage(selected)

  return (
    <div className="space-y-2">
      <Picker
        names={names}
        counts={counts}
        total={total}
        selected={selected}
        onSelect={onSelect}
        renderMark={(name) => <Mark brand={byName.get(name)} name={name} />}
      />
      {manageable && (
        <LogoManager
          name={selected}
          current={current}
          onBrandChange={onBrandChange}
          onError={onError}
        />
      )}
    </div>
  )
}

function Picker({
  names,
  counts,
  total,
  selected,
  onSelect,
  renderMark,
}: {
  names: string[]
  counts: ReadonlyMap<string, number>
  total: number
  selected: 'all' | string
  onSelect: (brand: 'all' | string) => void
  renderMark: (name: string) => ReactNode
}) {
  const root = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  // Options: "All brands" first, then every manufacturer that matches what
  // has been typed. Matching is on any word start, so "black" finds
  // Blackmagic Design and "design" does too.
  const options = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matches = needle
      ? names.filter((name) =>
          name
            .toLowerCase()
            .split(/[\s\-–—/]+/)
            .some((word) => word.startsWith(needle)) || name.toLowerCase().includes(needle),
        )
      : names
    return needle ? matches : ['all', ...matches]
  }, [names, query])

  // Opening always starts from a clean search with the first option live.
  function show() {
    setQuery('')
    setActive(0)
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    // The input is rendered on open; focus it once it exists.
    const frame = requestAnimationFrame(() => searchRef.current?.focus())
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const el = root.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  function choose(value: 'all' | string) {
    onSelect(value)
    setOpen(false)
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      return
    }
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        show()
      }
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => Math.min(index + 1, options.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = options[active]
      if (option) choose(option)
    }
  }

  // The closed control carries the name only; counts live in the list, and
  // the scope row above already says how many devices are in play.
  const label = selected === 'all' ? 'All brands' : selected

  return (
    <div ref={root} className="relative" onKeyDown={onKeyDown}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`Filter by brand: ${label}`}
        title={label}
        onClick={() => (open ? setOpen(false) : show())}
        className={cx(inputBase, 'flex w-full min-w-0 items-center gap-2 pr-2 text-left')}
      >
        {selected !== 'all' && renderMark(selected)}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {selected !== 'all' ? (
          // A span, not a button: buttons cannot nest. Keyboard users clear
          // with Escape-then-Enter on "All brands"; pointer users click here.
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear brand filter"
            title="Clear brand filter"
            onClick={(event) => {
              event.stopPropagation()
              choose('all')
            }}
            className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-muted hover:bg-hover hover:text-foreground"
          >
            <Icon name="close" className="h-3 w-3" />
          </span>
        ) : (
          <Icon name="chevronDown" className="h-4 w-4 shrink-0 text-muted" />
        )}
      </button>

      {open && (
        // The control is one half of a two-column row; the list spans the whole
        // row (200% plus the gap) so brand names are not truncated by a
        // half-width trigger.
        <div className="glass absolute left-0 top-full z-40 mt-1.5 w-[calc(200%+0.5rem)] overflow-hidden rounded-2xl p-1.5 shadow-[var(--shadow-float)]">
          <div className="relative">
            <Icon
              name="search"
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted"
            />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setActive(0)
              }}
              placeholder="Type a brand…"
              aria-label="Search brands"
              aria-controls={listId}
              aria-activedescendant={options[active] ? `${listId}-${active}` : undefined}
              role="combobox"
              aria-expanded="true"
              autoComplete="off"
              className={cx(inputBase, 'h-9 w-full pl-8 text-sm')}
            />
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-label="Brands"
            className="mt-1.5 max-h-64 overflow-y-auto [scrollbar-width:thin]"
          >
            {options.length === 0 && (
              <li className="px-2.5 py-3 text-center text-xs text-muted">No brand matches “{query}”</li>
            )}
            {options.map((option, index) => {
              const isAll = option === 'all'
              const chosen = option === selected
              return (
                <li
                  key={option}
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={chosen}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(option)}
                  className={cx(
                    'flex cursor-pointer items-center gap-2 rounded-xl px-2.5 py-1.5 text-sm',
                    index === active ? 'bg-hover' : '',
                    isAll && names.length > 0 && 'mb-1 border-b border-[var(--border)] pb-2',
                  )}
                >
                  {isAll ? (
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-hover text-muted">
                      <Icon name="equipment" className="h-3.5 w-3.5" />
                    </span>
                  ) : (
                    renderMark(option)
                  )}
                  <span className={cx('min-w-0 flex-1 truncate', chosen && 'font-medium')}>
                    {isAll ? 'All brands' : option}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-muted/70">
                    {isAll ? total : (counts.get(option) ?? 0)}
                  </span>
                  {chosen && <Icon name="check" className="h-3.5 w-3.5 shrink-0 text-foreground" />}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

/** A brand's logo where the platform has one, else a two-letter tile in a colour derived from the name. */
function Mark({ brand, name }: { brand: BrandDTO | undefined; name: string }) {
  if (brand?.logoUrl) {
    return (
      // Stays white in both themes: most vendor logos are dark ink on transparent.
      <span className="grid h-6 w-6 shrink-0 place-items-center overflow-hidden rounded-lg bg-white ring-1 ring-[var(--border)]">
        <Image src={brand.logoUrl} alt="" width={24} height={24} unoptimized className="h-5 w-5 object-contain" />
      </span>
    )
  }
  return (
    <span
      aria-hidden="true"
      style={brandTint(name)}
      className="grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[10px] font-semibold tracking-wide"
    >
      {brandInitials(name)}
    </span>
  )
}

/** Upload, replace or remove the selected brand's logo. Platform admins only. */
function LogoManager({
  name,
  current,
  onBrandChange,
  onError,
}: {
  name: string
  current: BrandDTO | null
  onBrandChange: (brand: BrandDTO) => void
  onError: (message: string) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  async function upload(file: File) {
    if (file.size > LOGO_MAX_BYTES) {
      onError(`Logos must be ${LOGO_MAX_BYTES / 1024} KB or smaller — this file is ${Math.ceil(file.size / 1024)} KB.`)
      return
    }
    setBusy(true)
    try {
      let brand = current
      if (!brand?.id) {
        const response = await fetch('/api/brands', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name }),
        })
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(payload.error ?? 'Could not create the brand')
        brand = payload as BrandDTO
      }
      const form = new FormData()
      form.append('logo', file)
      const response = await fetch(`/api/brands/${brand.id}/logo`, { method: 'PUT', body: form })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error ?? 'Could not upload the logo')
      onBrandChange(payload as BrandDTO)
    } catch (cause) {
      onError(errorMessage(cause, 'Could not upload the logo'))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!current?.id) return
    setBusy(true)
    try {
      const response = await fetch(`/api/brands/${current.id}/logo`, { method: 'DELETE' })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error ?? 'Could not remove the logo')
      onBrandChange(payload as BrandDTO)
    } catch (cause) {
      onError(errorMessage(cause, 'Could not remove the logo'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5 text-[11px] text-muted">
      {!current?.hasLogo && <span className="truncate">No logo yet</span>}
      <button
        type="button"
        className={cx(btn.sm, btn.secondary, 'ml-auto')}
        disabled={busy}
        onClick={() => fileInput.current?.click()}
        title={`PNG, SVG, WebP or JPEG, up to ${LOGO_MAX_BYTES / 1024} KB`}
      >
        <Icon name="upload" className="h-3.5 w-3.5" />
        {current?.hasLogo ? 'Replace logo' : 'Upload logo'}
      </button>
      {current?.hasLogo && (
        <button type="button" className={cx(btn.sm, btn.ghost)} disabled={busy} onClick={remove}>
          Remove logo
        </button>
      )}
      <input
        ref={fileInput}
        type="file"
        accept={LOGO_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void upload(file)
          event.target.value = ''
        }}
      />
    </div>
  )
}
